import axiosInstance from '../AxiosInstance';
import { getAccessToken } from '../../../features/auth/utils/token-storage';

export type LoginRole = 'user' | 'manager' | 'admin';
export type AppRole = 'user' | 'b2b_user' | 'b2b_admin';

const normalizeLoginRole = (value: unknown): LoginRole => {
    const normalized = String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/ı/g, 'i');
    if (normalized === 'admin') return 'admin';
    if (normalized === 'manager' || normalized === 'yonetici' || normalized === 'yönetici') return 'manager';
    return 'user';
};

const resolveAppRole = (loginRole: LoginRole, isAdmin: boolean): AppRole => {
    if (loginRole === 'admin' || isAdmin) return 'b2b_admin';
    if (loginRole === 'manager') return 'b2b_admin';
    return 'user';
};

/**
 * Bu uygulama HER ZAMAN connect urunudur; sunucu access token omrunu buna gore
 * seciyor (JWT_EXPIRES_IN_CONNECT, bkz. server/src/auth/auth.service.ts).
 *
 * ⚠️ Bilerek cagri noktalarinda degil BURADA ekleniyor: login uc ayri yerden
 *    cagriliyor (auth-slice, AuthActions, legacyActionsBridge) ve birinde
 *    unutulursa o akis sessizce kisa admin omrune duserdi.
 */
const CLIENT_PRODUCT = 'connect' as const;

export function signUp(payload) {
    return axiosInstance.post('/auth/register', { ...payload, client: CLIENT_PRODUCT });
}

export function verifyEmail(code: string) {
    return axiosInstance.post('/auth/verify-email', { code });
}

export function requestEmailVerification(email: string) {
    return axiosInstance.post('/auth/request-email-verification', { email });
}

export function login(payload) {
    return axiosInstance.post('/auth/login', { ...payload, client: CLIENT_PRODUCT });
}

export async function isCurrentUserAdmin() {
    try {
        await axiosInstance.get('/rbac/roles', { skipAuthRedirect: true } as any);
        return true;
    } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 403 || status === 404) {
            return false;
        }
        throw error;
    }
}

export function resolveForgotPasswordApp() {
    const explicit = String(process.env.REACT_APP_FORGOT_PASSWORD_APP || '').trim();
    if (explicit) return explicit;

    const appMode = String(process.env.REACT_APP_APP_MODE || 'auto').trim().toLowerCase();
    if (appMode === 'b2b') return 'web_user_b2b';
    if (appMode === 'user') return 'web_user_user';
    return 'web_user';
}

export function requestPasswordReset(email: string, app?: string) {
    const payload: Record<string, string> = { email };
    const appValue = String(app || '').trim() || resolveForgotPasswordApp();
    if (appValue) payload.app = appValue;
    return axiosInstance.post('/auth/forgot-password', payload);
}

export function requestEmailLogin(email: string, lang?: string) {
    return axiosInstance.post('/auth/email-login', { email, lang });
}

export function resetPassword(payload) {
    return axiosInstance.post('/auth/reset-password', payload);
}

export function formatError(errorResponse) {
    if (!errorResponse) return 'İşlem başarısız.';
    const fieldErrors = errorResponse?.errors;
    if (fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)) {
        const msgs: string[] = [];
        for (const [field, errs] of Object.entries(fieldErrors)) {
            const errList = Array.isArray(errs) ? errs : [errs];
            msgs.push(`${field}: ${errList.join(', ')}`);
        }
        if (msgs.length > 0) return msgs.join(' | ');
    }

    const message =
        errorResponse?.message ||
        errorResponse?.error ||
        (Array.isArray(errorResponse?.message) ? errorResponse.message.join(', ') : null) ||
        (typeof errorResponse === 'string' ? errorResponse : null);
    return typeof message === 'string' && message.trim() ? message : 'İşlem başarısız.';
}

export function saveTokenInLocalStorage(tokenDetails: any, opts?: { loginRole?: unknown; isAdmin?: boolean }) {
    const authData = tokenDetails?.data ?? tokenDetails ?? {};
    const expiresAt = authData?.expiresAt ? new Date(authData.expiresAt) : new Date(Date.now() + 60 * 60 * 1000);

    const loginRole = normalizeLoginRole(opts?.loginRole ?? authData?.loginRole);
    const isAdmin = Boolean(opts?.isAdmin ?? authData?.isAdmin);
    const appRole = resolveAppRole(loginRole, isAdmin);

    const normalized = {
        ...authData,
        expireDate: expiresAt.toISOString(),
        loginRole,
        isAdmin,
        appRole,
    };

    localStorage.setItem('userDetails', JSON.stringify(normalized));
    if (normalized.accessToken) {
        localStorage.setItem('accessToken', normalized.accessToken);
    }
    return normalized;
}

let sessionLogoutTimer: ReturnType<typeof setTimeout> | null = null;

export function clearLogoutTimer() {
    if (sessionLogoutTimer !== null) {
        clearTimeout(sessionLogoutTimer);
        sessionLogoutTimer = null;
    }
}

/**
 * Oturumun TEK otomatik cikis zamanlayicisi.
 *
 * ⚠️ Eskiden her giris yeni bir zamanlayici kuruyor ve hicbiri iptal
 *    edilmiyordu: onceki oturumun zamanlayicisi yeni giristen sonra calisip
 *    kullaniciyi "Giris basarili" mesajinin hemen ardindan login'e atiyordu.
 *    Artik her kurulum oncekini iptal eder; zamanlayici kuruldugu oturum
 *    degismisse (yeni giris) hicbir sey yapmaz. Cikis suresi degismedi.
 */
export function runLogoutTimer(dispatch: any, timer: number, onLogout: () => any) {
    clearLogoutTimer();

    const scheduledForToken = getAccessToken();
    sessionLogoutTimer = setTimeout(() => {
        sessionLogoutTimer = null;
        if (getAccessToken() !== scheduledForToken) return;
        dispatch(onLogout());
    }, timer);
}

export function checkAutoLogin(
    dispatch: any,
    navigate: any,
    actions: { onLoginConfirmed: (payload: any) => any; onLogout: (navigate: any) => any },
) {
    const tokenDetailsString = localStorage.getItem('userDetails');
    if (!tokenDetailsString) {
        dispatch(actions.onLogout(navigate));
        return;
    }

    let tokenDetails: any;
    try {
        tokenDetails = JSON.parse(tokenDetailsString);
    } catch {
        dispatch(actions.onLogout(navigate));
        return;
    }

    const expireDateValue = tokenDetails.expireDate || tokenDetails.expiresAt;
    const expireDate = expireDateValue ? new Date(expireDateValue) : null;
    const now = new Date();

    if (!tokenDetails.accessToken || !expireDate || Number.isNaN(expireDate.getTime()) || now > expireDate) {
        dispatch(actions.onLogout(navigate));
        return;
    }

    dispatch(actions.onLoginConfirmed(tokenDetails));
    runLogoutTimer(dispatch, expireDate.getTime() - now.getTime(), () => actions.onLogout(navigate));
}
